import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { PrizeCoverImage } from './components/PrizeCoverImage';
import RankingSelector from './components/RankingSelector';
import SideContentInfo from './components/SideContentInfo';

import { Loader, Tabs } from '@/components';
import { Alert } from '@/components/Alert';
import { Layout } from '@/components/GenericsTournaments';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTapTourney from '@/hooks/useTapTourney';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import { viewTapTourneyData } from '@/services/tda';
import { EnumDevice } from '@/types';
import { SelectedScreen } from '@/types/editProfile';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

const ViewTapTourney = () => {
  const { tapTourneyData, updateTapProperties } = useTapTourney();
  const { isDesktop, isMobile } = useIsDesktop();
  const [touneyTitle, setTouneyTitle] = useState('');
  // Nuevo state para guardar la data lateral fija
  const [fixedLateralData, setFixedLateralData] = useState<any>(undefined);

  // este se va a usar para las distintas tablas
  const { tab: tabTable, handleTabChanges: handleTabTable } =
    useHandleChangesTabs();

  const navigate = useNavigate();
  const toast = useToast();

  const canFetchData = !!(
    tapTourneyData &&
    tapTourneyData.tourneyId &&
    tapTourneyData.tourneyDate
  );

  const { data: dataInit, isLoading } = useQuery(
    ['initDataTAP', tapTourneyData?.tourneyId, tapTourneyData?.tourneyDate?.id],
    async () => {
      if (
        tapTourneyData &&
        tapTourneyData.tourneyId &&
        tapTourneyData.tourneyDate?.id
      ) {
        return await viewTapTourneyData(
          tapTourneyData.tourneyId,
          tapTourneyData.tourneyDate.id
        );
      }
      throw new Error('Datos necesarios no disponibles');
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data) => {
        if (data?.nombre !== touneyTitle) {
          setTouneyTitle(data?.nombre ?? '');
        }
        // Setear fixedLateralData solo la primera vez
        if (!fixedLateralData) {
          setFixedLateralData(data);
        }
        // Setear el orden de la primera fecha
        updateTapProperties({
          tourneyDate: {
            ...tapTourneyData?.tourneyDate,
            id: tapTourneyData?.tourneyDate?.id ?? 0,
            ordenFC: data.ordenFC,
          },
        });
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.show(err.message);
      },
      enabled: canFetchData,
    }
  );

  const tableTabs: DefaultTabsProps['tabs'] = [
    { name: 'Fecha', disabled: false },
    {
      name: 'Torneo General',
      disabled: false,
    },
  ];

  const onHandleTableTabsChanges = (tab: TabType) => {
    handleTabTable(tab);
  };

  const mobileLeftContent = () => {
    if (isMobile) {
      return {
        backAction: () => {
          navigate(ROUTES.friendsTournamentPrizes, {
            state: { showtourneys: true },
          });
        },
      };
    }
    return {
      showMenuAction: true,
      items: true,
    };
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {<Loader isVisible={isLoading} timeout={true} />}
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.torneodeamigospremios}
        backButton={isDesktop}
        backButtonAction={() =>
          navigate(ROUTES.friendsTournamentPrizes, {
            state: { showtourneys: true },
          })
        }
        className="px-4"
        coverTop={() => (
          <>
            <PrizeCoverImage
              prize={tapTourneyData?.tourneyPrize}
              touneyData={dataInit}
            />
            {isMobile && !tapTourneyData?.llenoFormBancario && (
              <Alert
                className="bg-gdt-blue w-full lg:mt-5 "
                message={
                  <div className="flex flex-wrap">
                    <span className="me-1">
                      Cargá los datos de tu CBU por si resultás ser el ganador.{' '}
                    </span>
                    <strong
                      className="cursor-pointer flex-auto whitespace-nowrap"
                      onClick={() =>
                        navigate(ROUTES.editAccount.myCBU, {
                          state: { selectedScreen: 'MyCBU' } as SelectedScreen,
                        })
                      }
                    >
                      Completar
                    </strong>
                  </div>
                }
                variant={EnumDevice.Mobile}
              />
            )}
          </>
        )}
        main={() => (
          <div>
            {isDesktop && (
              <div className="flex flex-col items-start gap-2 mb-6">
                <p className="text-[24px] font-bold my-4">{touneyTitle}</p>
              </div>
            )}
            <RankingSelector
              activeTab={tabTable}
              fixedLateralData={fixedLateralData}
              tableTabs={tableTabs}
              tourneyData={dataInit}
              onHandleTableTabsChanges={onHandleTableTabsChanges}
            />
          </div>
        )}
        mobile={{
          leftContent: mobileLeftContent(),
          centerContent: {
            title: dataInit?.nombre,
          },
        }}
        sidebar={() => <SideContentInfo tourneyData={fixedLateralData} />}
        tabs={() => {
          if (isMobile) {
            return (
              <Tabs
                activeTab={tabTable}
                tabs={tableTabs}
                onClick={onHandleTableTabsChanges}
              />
            );
          }
        }}
      />
    </>
  );
};

export default ViewTapTourney;
